import { type FormEvent, useState } from "react";

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

/** The name and email of someone being invited, already trimmed. */
export interface NewPerson {
  email: string;
  displayName: string;
}

/** Where a failed submission explains itself: under the email field or above the actions. */
export interface SubmitFailure {
  field: "email" | "form";
  text: string;
}

export interface NewPersonForm {
  email: string;
  displayName: string;
  setEmail: (email: string) => void;
  setDisplayName: (displayName: string) => void;
  emailError: string | null;
  error: string | null;
  loading: boolean;
  /** Both name and email have been typed. */
  filled: boolean;
  submit: (event: FormEvent | undefined, send: (person: NewPerson) => Promise<void>, explain: (err: unknown) => SubmitFailure) => Promise<void>;
}

/** State of a "name + email" invitation form: checks the email, sends it and shows what went wrong. */
export function useNewPersonForm(): NewPersonForm {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(
    event: FormEvent | undefined,
    send: (person: NewPerson) => Promise<void>,
    explain: (err: unknown) => SubmitFailure,
  ): Promise<void> {
    event?.preventDefault();
    setError(null);
    setEmailError(null);
    if (!EMAIL_PATTERN.test(email.trim())) {
      setEmailError("Enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      await send({ email: email.trim(), displayName: displayName.trim() });
    } catch (err) {
      const failure = explain(err);
      if (failure.field === "email") setEmailError(failure.text);
      else setError(failure.text);
    } finally {
      setLoading(false);
    }
  }

  const filled = email.trim() !== "" && displayName.trim() !== "";
  return { email, displayName, setEmail, setDisplayName, emailError, error, loading, filled, submit };
}
